import { Component, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { Toast } from 'primeng/toast';
import { IInstallation } from '../../../models/installation';
import { Auth, UserRoles } from '../../../services/auth';
import { Controller } from '../../../services/controller';
import { Installation } from '../../../services/installation';
import { User } from '../../../services/user';

@Component({
  selector: 'app-installations',
  imports: [
    Toast,
    TableModule,
    IconField,
    InputIcon,
    InputTextModule,
    FormsModule,
    CheckboxModule,
    Button,
    Dialog,
    MultiSelectModule,
  ],
  templateUrl: './installations.html',
  styleUrl: './installations.css',
  providers: [MessageService],
})
export class Installations implements OnInit {
  installationService = inject(Installation);
  readonly authService = inject(Auth);
  userService = inject(User);
  controllerService = inject(Controller);
  private messageService = inject(MessageService);
  installation!: IInstallation;
  installationDialog: boolean = false;
  submitted: boolean = false;
  allUsersSelected = false;
  readonly isSuperAdmin = this.authService.hasRole(UserRoles.SUPER_ADMIN);

  installationWithCounts = computed(() => {
    const installations = this.installationService.installations();

    return installations.map((i) => ({
      ...i,
      amountOfLinkedBoxes: i.box_ids.length,
      amountOfAccountsVisible: i.user_ids.length,
    }));
  });

  controllersWithDisplayLabel = computed(() => {
    const controllers = this.controllerService.controllers();

    return controllers.map((c) => ({
      ...c,
      displayLabel: `${c.serialNumber}: ${c.name}`,
    }));
  });

  controllersWithDisplayLabelAndUnused = computed(() => {
    const controllers = this.controllerService.controllers();

    return controllers
      .filter((c) => c.installationId === 1 || c.installationId === 2)
      .map((c) => ({
        ...c,
        displayLabel: `${c.serialNumber}: ${c.name}`,
      }));
  });

  ngOnInit(): void {
    this.installationService.getInstallationList();
    this.userService.getBasicUserList();
    this.controllerService.getControllerList();
  }

  addInstallation() {
    this.installation = this.createEmptyInstallation();
    this.installationDialog = true;
  }

  editInstallation(installation: IInstallation) {
    this.installation = structuredClone(installation);
    this.allUsersSelected =
      this.installation.user_ids.length ===
      this.userService.basicUsers().length;

    this.installationDialog = true;
  }

  deleteInstallation(installation: IInstallation) {
    this.installationService.deleteInstallation(installation).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succesful',
          detail: 'Installation succesfully removed',
          life: 3000,
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Deletion failed',
          life: 3000,
        });
      },
    });
  }

  hideDialog() {
    this.installationDialog = false;
    this.installation = this.createEmptyInstallation();
    this.submitted = false;
  }

  saveInstallation() {
    this.submitted = true;
    if (!this.installation.name.trim()) {
      return;
    }

    if (this.installation.id !== 0) {
      // edit installation
      this.installationService.updateInstallation(this.installation).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succesful',
            detail: 'Installation updated',
            life: 3000,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Update failed',
            life: 3000,
          });
        },
      });
    } else {
      // new installation
      this.installationService.addInstallation(this.installation).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successful',
            detail: 'Installation added',
            life: 3000,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Adding new installation failed',
            life: 3000,
          });
        },
      });
    }
    this.afterSave();
  }

  toggleSelectAllUsers() {
    const users = this.userService.basicUsers();

    if (this.allUsersSelected) {
      // Select all
      this.installation.user_ids = users.map((u) => u.id);
    } else {
      // Deselect all
      this.installation.user_ids = [];
    }
  }

  updateSelectAllState() {
    const users = this.userService.basicUsers();

    this.allUsersSelected = this.installation.user_ids?.length === users.length;
  }

  private createEmptyInstallation(): IInstallation {
    return {
      id: 0,
      name: '',
      user_ids: [],
      box_ids: [],
    };
  }

  private afterSave() {
    this.installationDialog = false;
    this.allUsersSelected = false;
    this.installation = this.createEmptyInstallation();
  }
}
